/**
 * The converter's context, with defaults (git sync Stage 2, S2.1). Pure — no
 * editor — so the MCP read path can use it without reaching the editor's
 * node classes (see readable.js).
 *
 *   mediaPath(node) → repo path of the file, or null = "kept in Apiboo" (default)
 *   pageLinkHref(node) → link target (default: apiboo://documents/<scope>/<id>)
 *   pageLinkFromHref(href) → { docId, scopeType, teamId } | null
 *   mediaFor({ path?, src? }) → the existing image node's fields | null
 *   onNewMedia({ pendingRef, path }) — a file that came in through git
 *   isMediaHref(href) → true when a media link points into the repository
 *     (default: a plain relative path without `..`)
 */
const PAGE_HREF = /^apiboo:\/\/documents\/(personal|device|team:([^/]+))\/([^/?#]+)$/;

export const defaultPageLinkHref = (n) =>
  `apiboo://documents/${n.scopeType === 'team' ? `team:${n.teamId}` : n.scopeType || 'personal'}/${n.docId}`;

export function* walkNodes(nodes) {
  for (const n of nodes || []) {
    yield n;
    if (Array.isArray(n.children)) yield* walkNodes(n.children);
  }
}

export const childrenOf = (input) => (Array.isArray(input) ? input : (input?.blocks || []).map((b) => b.node));

/** @param {Array} previousNodes  the document as it is now — its images are "known" (no re-upload) */
export function withDefaults(ctx = {}, previousNodes = []) {
  const mediaPath = ctx.mediaPath || (() => null);
  const known = new Map();
  for (const n of walkNodes(previousNodes)) {
    if (n.type !== 'doc-image') continue;
    if (n.src) known.set(`src:${n.src}`, n);
    if (n.pendingRef) known.set(`ref:${n.pendingRef}`, n);
    const p = mediaPath(n);
    if (p) known.set(`path:${p}`, n);
  }
  return {
    mediaPath,
    pageLinkHref: ctx.pageLinkHref || defaultPageLinkHref,
    pageLinkFromHref: (href) => {
      const own = ctx.pageLinkFromHref ? ctx.pageLinkFromHref(href) : null;
      if (own) return own;
      const m = PAGE_HREF.exec(String(href || ''));
      if (!m) return null;
      if (m[2]) return { docId: decodeURIComponent(m[3]), scopeType: 'team', teamId: Number(m[2]) || m[2] };
      return { docId: decodeURIComponent(m[3]), scopeType: m[1], teamId: null };
    },
    onNewMedia: ctx.onNewMedia,
    isMediaHref: ctx.isMediaHref,
    mediaFor: ({ path, src, pendingRef }) => {
      const hit =
        (src && known.get(`src:${src}`)) ||
        (pendingRef && known.get(`ref:${pendingRef}`)) ||
        (path && known.get(`path:${path}`));
      if (hit) return hit;
      return ctx.mediaFor ? ctx.mediaFor({ path, src, pendingRef }) : null;
    },
  };
}
